<template>
  <div class="creative-preview">
    <header class="toolbar">
      <label class="toolbar__upload">
        <input
          type="file"
          accept=".html,text/html"
          class="toolbar__input"
          @change="onFileChange"
        />
        Выбрать файл
      </label>
      <span v-if="fileName" class="toolbar__info">
        {{ fileName }}
        <template v-if="adSize">
          — {{ adSize.width }} × {{ adSize.height }}</template
        >
      </span>
      <button
        v-if="iframeSrc"
        type="button"
        class="toolbar__clear"
        @click="clearCreative"
      >
        Очистить
      </button>
    </header>

    <article class="article">
      <h1 class="article__title">Тестовая статья</h1>

      <p class="article__paragraph">
        В последние годы рынок цифровой рекламы продолжает активно развиваться.
        Бренды всё чаще ищут новые способы взаимодействия с аудиторией,
        экспериментируют с форматами и стремятся получить обратную связь от
        пользователей в момент потребления контента. Именно поэтому тестирование
        креативов в реальном контексте публикации становится важной частью
        подготовки кампании.
      </p>

      <div class="creative-slot">
        <iframe
          v-if="iframeSrc && adSize"
          :src="iframeSrc"
          :width="adSize.width"
          :height="adSize.height"
          class="creative-slot__iframe"
          sandbox="allow-scripts allow-same-origin"
          :title="fileName || 'Рекламный креатив'"
        />
        <p v-else class="creative-slot__placeholder">
          Загрузите HTML-файл креатива (кнопка сверху)
        </p>
      </div>

      <p class="article__paragraph">
        После размещения баннера между абзацами текста можно оценить, как
        креатив воспринимается на разных устройствах и разрешениях экрана.
        Обратите внимание на читаемость, удобство нажатия на элементы управления
        и общее впечатление от интеграции рекламного блока в поток материала.
        Такой подход помогает выявить проблемы до запуска кампании в продакшене.
      </p>
    </article>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from "vue";

interface AdSize {
  width: number;
  height: number;
}

const fileName = ref<string | null>(null);
const iframeSrc = ref<string | null>(null);
const adSize = ref<AdSize | null>(null);

function parseAdSize(html: string): AdSize {
  const match =
    html.match(
      /<meta[^>]+name=["']ad\.size["'][^>]+content=["']width=(\d+),height=(\d+)["']/i,
    ) ||
    html.match(
      /content=["']width=(\d+),height=(\d+)["'][^>]+name=["']ad\.size["']/i,
    );

  return match
    ? { width: +match[1], height: +match[2] }
    : { width: 300, height: 250 };
}

function revokeBlobUrl() {
  if (iframeSrc.value?.startsWith("blob:")) {
    URL.revokeObjectURL(iframeSrc.value);
  }
}

function clearCreative() {
  revokeBlobUrl();
  iframeSrc.value = null;
  fileName.value = null;
  adSize.value = null;
}

async function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const html = await file.text();
  revokeBlobUrl();

  iframeSrc.value = URL.createObjectURL(
    new Blob([html], { type: "text/html" }),
  );
  fileName.value = file.name;
  adSize.value = parseAdSize(html);

  input.value = "";
}

onUnmounted(() => {
  revokeBlobUrl();
});
</script>

<style lang="scss" scoped>
.creative-preview {
  min-height: 100vh;
  background: #f5f5f5;
}

.toolbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  &__upload {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    background: #6428ef;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #5520d4;
    }
  }

  &__input {
    display: none;
  }

  &__info {
    font-size: 14px;
    color: #555;
  }

  &__clear {
    padding: 8px 16px;
    font-size: 14px;
    color: #333;
    background: #eee;
    border: none;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #e0e0e0;
    }
  }
}

.article {
  max-width: 680px;
  margin: 0 auto;
  padding: 32px 20px 64px;
  font-family: Georgia, "Times New Roman", serif;
  color: #222;

  &__title {
    margin: 0 0 24px;
    font-size: 28px;
    line-height: 1.25;
    font-weight: 700;
  }

  &__paragraph {
    margin: 0 0 20px;
    font-size: 17px;
    line-height: 1.65;
  }
}

.creative-slot {
  margin: 24px 0;
  text-align: center;

  &__iframe {
    display: block;
    margin: 0 auto;
    border: 1px solid #ddd;
    background: #fff;
  }

  &__placeholder {
    margin: 0;
    padding: 48px 20px;
    font-family: Inter, Arial, sans-serif;
    font-size: 15px;
    color: #888;
    background: #fff;
    border: 2px dashed #ccc;
    border-radius: 8px;
  }
}
</style>
